import React from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { ThemeProvider, createTheme } from "@mui/material";
import "./MaterialTable.css";
// import { padding } from "@mui/system";

/* -------------------- CUSTOM DARK THEME -------------------- */
const customTheme = createTheme({
   typography: {
    fontFamily: "f7",
  },
  palette: {
    mode: "dark",
    primary: {
      main: "#4f46e5", // Indigo highlight
    },
    background: {
      default: "#ffffff",
      paper: "#ffffff",
    },
    text: {
      primary: "#ffffff",
      secondary: "#9ca3af",
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: "#ffffff !important",
          color: "#000000 !important",
          borderRadius: "0px",
          marginTop: 25,
          
          // padding: "6px 8px",
         
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: "#ffffff",
         
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          // padding:"6px 6px",
          background:'#134eb3',
          color: "#9ca3af",
          fontWeight: 600,
          borderBottom: "1px solid #D5D5D5",
        },
        body: {
          // padding:"6px 6px",
          // color: "#e5e7eb",
          color:"black",
          borderBottom: "1px solid #D5D5D5",
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: "#ffffff !important",
          },
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: "#ffffff",
          // borderBottom: "1px solid #D5D5D5",
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: "#fff",
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: "#fff",
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: "#ffffff",
          // border: "1px solid #333",
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: "#ffffff",
          // border: "1px solid #D5D5D5",
        },
      },
    },
  },
});

/** -------------------- MAIN COMPONENT -------------------- */
const MaterialTable = ({
  columns,
  data = [],
  enableRowSelection = false,   // 👈 ADD THIS LINE
  getRowId,
  onRowSelectionChange,
  paginationEnable = false,
  enableGlobalFilter = false,
  showRowsPerPage = false,
  
  rowCount = 0,
  pagination = 1,
  setPagination = () => { },
  pageSize = null,
  loading = false,
}) => {
  const [rowSelection, setRowSelection] = React.useState({});
  const lastSelectionRef = React.useRef("");
  const columnOrder = columns.map((col) => col.id ?? col.accessorKey);
  const safePageSize = Number.isFinite(Number(pageSize)) && Number(pageSize) > 0
    ? Math.floor(Number(pageSize))
    : 10;
  const safeRowCount = Number.isFinite(Number(rowCount)) && Number(rowCount) > 0
    ? Math.floor(Number(rowCount))
    : 0;
  const safeCurrentPage = Number.isFinite(Number(pagination)) && Number(pagination) > 0
    ? Math.floor(Number(pagination))
    : 1;
  const [pageInput, setPageInput] = React.useState(String(safeCurrentPage));

  React.useEffect(() => {
    setPageInput(String(safeCurrentPage));
  }, [safeCurrentPage]);

  const tableConfig = {
    columns,
    data  ,
        layoutMode: "semantic",
           
    defaultColumn: {
  size: 60,
  minSize: 40,
},

    enableRowSelection, // ✅ ADD THIS  and add it in the page where u need it by calling it
    enableSelectAll: enableRowSelection,    // ✅ optional (select all checkbox)
    getRowId,
    onRowSelectionChange: setRowSelection,
      muiTableBodyRowProps: {
  sx: {
    "&.Mui-selected": {
      backgroundColor: "#ffffff !important",
    },
    "&.Mui-selected:hover": {
      backgroundColor: "#ffffff !important",
    },
  },
},

     enableGlobalFilter,
     
    enableFilters: enableGlobalFilter,
    enableHiding: true,
    columnFilterDisplayMode: "popover",
    paginationDisplayMode: "pages",
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enablePagination: paginationEnable,
    enableColumnOrdering: false,
    enableFullScreenToggle: false,
    autoResetPageIndex: false,
    initialState: { density: "compact" },

    muiTableHeadCellProps: {
      align: "center",
         },
    muiTableBodyCellProps: {
      align: "center",
          },

    muiTablePaperProps: {
      elevation: 0,
    },

    muiTopToolbarProps: {
        sx: { borderBottom: '1px solid #D5D5D5' },
    },

    muiBottomToolbarProps: {
      sx: { borderTop: "0px solid #D5D5D5" },
    },

    muiPaginationProps: {
      showRowsPerPage,
    },
    state: {
      isLoading: loading,
      columnOrder,
      rowSelection,
    },
//     muiTableBodyRowProps: ({ row }) => ({
//   sx: {
//     backgroundColor: row.getIsSelected()
//       ? "#ffffff !important"   // 👈 change to white
//       : "#ffffff",
//   },
// }),

  };

  /* ---------------- SERVER SIDE PAGINATION ---------------- */
  if (!paginationEnable) {
    tableConfig.rowCount = safeRowCount;
    tableConfig.manualPagination = true;
    tableConfig.onPaginationChange = (updater) => {
      const currentPagination = {
        pageIndex: safeCurrentPage - 1,
        pageSize: safePageSize,
      };
      const nextPagination =
        typeof updater === "function" ? updater(currentPagination) : updater;

      if (nextPagination && Number.isFinite(Number(nextPagination.pageIndex))) {
        setPagination(Number(nextPagination.pageIndex) + 1);
      }
    };
    tableConfig.state = {
      ...tableConfig.state,
      pagination: {
        pageIndex: safeCurrentPage - 1,
        pageSize: safePageSize,
      },
    };
  }

  const table = useMaterialReactTable(tableConfig);

  React.useEffect(() => {
    if (!enableRowSelection || typeof onRowSelectionChange !== "function") return;

    const selectedIds = Object.keys(rowSelection).filter((k) => rowSelection[k]);
    const signature = selectedIds.join(",");
    if (signature == lastSelectionRef.current) return;
    lastSelectionRef.current = signature;

    if (selectedIds.length == 0) {
      onRowSelectionChange([], rowSelection);
      return;
    }

    let selectedRows = [];
    if (typeof getRowId === "function") {
      const byId = new Map(
        data.map((row, idx) => [String(getRowId(row, idx)), row])
      );
      selectedRows = selectedIds.map((id) => byId.get(String(id))).filter(Boolean);
    } else {
      selectedRows = selectedIds
        .map((id) => data[Number(id)])
        .filter(Boolean);
    }

    onRowSelectionChange(selectedRows, rowSelection);
  }, [data, enableRowSelection, getRowId, onRowSelectionChange, rowSelection]);

  const totalPages = Math.max(1, Math.ceil(safeRowCount / safePageSize));

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages || page === safeCurrentPage) return;
    setPagination(page);
  };

  const submitPageInput = () => {
    if (pageInput.trim() === "") {
      setPageInput(String(safeCurrentPage));
      return;
    }
    const parsed = Math.max(1, Math.floor(Number(pageInput)));
    if (!Number.isFinite(parsed)) {
      setPageInput(String(safeCurrentPage));
      return;
    }
    const nextPage = Math.min(parsed, totalPages);
    handlePageChange(nextPage);
  };

  const handlePageInputChange = (event) => {
    const next = event.target.value.replace(/\D/g, "");
    setPageInput(next);
  };

  const handlePageInputKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      submitPageInput();
    }
  };

  return (
    <ThemeProvider theme={customTheme}>
      <MaterialReactTable table={table}  />

      {/* ---------- CUSTOM PAGINATION ---------- */}
      {!paginationEnable && (
        <div className="tab_pagin">
          <nav className="pagination-wrapper">
            <button
              className="page-btn"
              disabled={safeCurrentPage === 1}
              onClick={() => handlePageChange(safeCurrentPage - 1)}
            >
              Previous
            </button>

            <div className="pagination-input-wrapper">
              <input
                type="text"
                inputMode="numeric"
                value={pageInput}
                onChange={handlePageInputChange}
                onKeyDown={handlePageInputKeyDown}
                onBlur={submitPageInput}
                aria-label="Current page"
              />
              <span className="page-total">of {totalPages}</span>
            </div>

            <button
              className="page-btn"
              disabled={safeCurrentPage === totalPages}
              onClick={() => handlePageChange(safeCurrentPage + 1)}
            >
              Next
            </button>
          </nav>
        </div>
      )}
    </ThemeProvider>
  );
};

export default MaterialTable;
